import type { Ref } from 'react';
import { IconButton } from '@mui/material';
import StarRounded from '@mui/icons-material/StarRounded';
import StarBorderRounded from '@mui/icons-material/StarBorderRounded';

export interface PreferredRadioProps {
  /** Whether this row is the single preferred selection. */
  selected: boolean;
  /** Disabled rows cannot be preferred (e.g. a carrier that isn't enabled). */
  disabled?: boolean;
  /** Toggle intent — selecting this row (or clearing it when already selected). */
  onSelect: () => void;
  /** Accessible label (translated copy). */
  ariaLabel?: string;
  /** Native tooltip text (translated copy). */
  title?: string;
  ref?: Ref<HTMLButtonElement>;
}

/**
 * A single-select gold star used as a radio across table rows: exactly one row
 * may be marked preferred. Filled when selected, hollow otherwise; disabled
 * rows render dimmed and are not selectable. The caller enforces the
 * at-most-one invariant by clearing the prior selection.
 */
export function PreferredRadio({
  selected,
  disabled = false,
  onSelect,
  ariaLabel,
  title,
  ref,
}: PreferredRadioProps) {
  return (
    <IconButton
      ref={ref}
      role="radio"
      aria-checked={selected}
      aria-label={ariaLabel}
      title={title}
      disabled={disabled}
      onClick={() => !disabled && onSelect()}
      size="small"
      sx={{
        // Prototype gold (#E0A106) for the filled star — a one-off decorative
        // affordance not represented in the semantic theme ramps.
        color: selected ? '#E0A106' : 'text.secondary',
        opacity: disabled ? 0.32 : 1,
      }}
    >
      {selected ? <StarRounded fontSize="small" /> : <StarBorderRounded fontSize="small" />}
    </IconButton>
  );
}
